import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import capitalize from 'lodash/capitalize'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import { MoreVert } from '@mui/icons-material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { getFormattedGracePeriod } from 'components/pricing/RentalFees/common/utils'
import { useLazyGetConfiguredServicesByRentalFeeQuery } from 'api/configured-service/configuredServiceRentalFeesCrud'

import T from 'T'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import RentalFeeStatItem from 'components/pricing/RentalFees/RentalFeesListing/RentalFeeStatItem'
import ActionMenu from './ActionMenu'
import ConfiguredServiceList from '../../configured-services/ConfiguredServiceList'

const DESKTOP_MIN_WIDTH = { lg: '100px', xl: '150px' }

const RentalFeeAccordionItem = ({ accountId, rentalFee = {} }) => {
  const [getConfiguredServicesByRentalFee, { data = [], isLoading }] = useLazyGetConfiguredServicesByRentalFeeQuery()
  const { feeName = '', pricingPeriod = '', amountCents = 0, businessLine = '', gracePeriodDays = 0 } = rentalFee
  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.only('md'))
  const isXs = useMediaQuery(theme.breakpoints.down('sm'))
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))

  const [withDeactivated, setWithDeactivated] = useState(false)
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const toggleIsExpanded = () => setIsExpanded(!isExpanded)

  const formattedGracePeriod = useMemo(() => getFormattedGracePeriod(gracePeriodDays), [gracePeriodDays])
  const formattedBusinessLine = businessLine || 'None'

  const handleOpenActionMenu = event => setActionMenuAnchorEl(event.currentTarget)

  const handleCloseActionMenu = () => setActionMenuAnchorEl(null)

  const handleToggleStatus = () => {
    setWithDeactivated(prev => !prev)
    handleCloseActionMenu()
  }

  useEffect(() => {
    if (isExpanded) {
      getConfiguredServicesByRentalFee({ rentalFeeId: rentalFee.rentalFeeId, accountId })
    }
  }, [isExpanded])

  return (
    <>
      <Accordion
        elevation={0}
        expanded={isExpanded}
        sx={{
          '&.Mui-expanded': { m: 0 },
          '& .MuiAccordionSummary-content.Mui-expanded, & .MuiAccordionSummary-content': { mb: 0, mt: 0 },
          '&:before': { display: 'none' },
        }}
        disablePointer
        expandIcon={undefined}
        iconGap={false}
      >
        <AccordionSummary sx={{ p: 0 }}>
          <ListItem
            secondaryAction={
              <IconButton onClick={handleOpenActionMenu}>
                <MoreVert />
              </IconButton>
            }
          >
            <ListItemIcon sx={{ minWidth: 'auto' }}>
              <IconButton onClick={toggleIsExpanded}>{isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}</IconButton>
            </ListItemIcon>
            <Box height="100%" width="100%">
              <ListItem>
                <ListItemText
                  disableTypography
                  primary={
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="h4" color="textPrimary" fontWeight={600} noWrap>
                        {feeName}
                      </Typography>
                      <Divider orientation="vertical" flexItem />
                      <Typography variant="h4" color="textPrimary" fontWeight={600}>
                        <HHDisplayMoney value={amountCents} />
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Grid container size={12}>
                      <RentalFeeStatItem
                        xs={12}
                        sm={6}
                        md={4}
                        lg="auto"
                        primary={formattedGracePeriod}
                        secondary={capitalize(T.GRACE_PERIOD)}
                        divider
                        sx={{ minWidth: DESKTOP_MIN_WIDTH }}
                      />
                      <RentalFeeStatItem
                        xs={12}
                        sm={6}
                        md={4}
                        lg="auto"
                        primary={capitalize(pricingPeriod)}
                        secondary={capitalize(T.PRICING_PERIOD)}
                        divider={isXs || isTablet || isDesktop}
                        sx={{ minWidth: DESKTOP_MIN_WIDTH }}
                      />
                      <RentalFeeStatItem xs={12} sm={6} md={4} lg primary={formattedBusinessLine} secondary={capitalize(T.BUSINESS_LINE)} />
                    </Grid>
                  }
                />
              </ListItem>
            </Box>
          </ListItem>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Divider />
          <Box my={2} pl={7}>
            <ConfiguredServiceList
              isLoading={isLoading}
              accountId={accountId}
              services={data}
              withDeactivated={withDeactivated}
              noRowsText="No services linked to this rental fee"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
      <ActionMenu
        anchorEl={actionMenuAnchorEl}
        withDeactivated={withDeactivated}
        onToggleStatus={handleToggleStatus}
        onClose={handleCloseActionMenu}
      />
    </>
  )
}

RentalFeeAccordionItem.propTypes = {
  accountId: PropTypes.string.isRequired,
  rentalFee: PropTypes.object,
}

export default RentalFeeAccordionItem
